import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './participanteeditpage.module.css';

// API
import { useParticipanteService } from '../../services/participanteServices';

function ParticipanteEditPage() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idParticipante } = useParams();
  const { pathname } = useLocation();

  const { obterParticipantePorId, atualizarParticipante } = useParticipanteService();

  const isEditing = !!idParticipante && pathname.includes('atualizar');

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    celular: '',
    senha: '',
  });

  const [idTurmaAtual, setIdTurmaAtual] = useState('');

  const formatarCelular = (valor) => {
    const numeros = String(valor || '').replace(/\D/g, '').slice(0, 11);
    const parte1 = numeros.slice(0, 2);
    const parte2 = numeros.slice(2, 7);
    const parte3 = numeros.slice(7, 11);

    if (numeros.length <= 2) return `(${parte1}`;
    if (numeros.length <= 7) return `(${parte1}) ${parte2}`;
    return `(${parte1}) ${parte2}-${parte3}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'celular') {
      const numeros = value.replace(/\D/g, '');
      setFormData((prev) => ({
        ...prev,
        celular: formatarCelular(numeros),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) {
      showToast('Rota de criação não disponível nesta página.', 3000, 'warning');
      return;
    }

    showLoader('Atualizando participante');

    const celularSemMascara = formData.celular.replace(/\D/g, '');

    if (celularSemMascara.length !== 11) {
      showToast('O celular deve conter 11 dígitos!', 3000, 'error');
      hideLoader();
      return;
    }

    const participanteJson = {
      id: idParticipante,
      nome: formData.nome,
      email: formData.email,
      celular: celularSemMascara,
      loginPor: 'celular',
      senha: formData.senha || '',
    };

    if (idTurmaAtual) {
      participanteJson.idTurma = idTurmaAtual;
    }

    const response = await atualizarParticipante(participanteJson);

    if (response.success) {
      showToast('Participante atualizado com sucesso!', 3000, 'success');
      navigate('/participantes');
    } else {
      showToast('Erro ao atualizar participante.', 3000, 'error');
      if (response.message?.toLowerCase().includes('token')) logout();
    }

    hideLoader();
  };

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando dados do participante');

      const participanteResp = await obterParticipantePorId(idParticipante);

      if (participanteResp.success && participanteResp.data) {
        const participante = participanteResp.data;
        const celularFormatado = formatarCelular(participante.celular || '');

        setFormData({
          nome: participante.nome || '',
          email: participante.email || '',
          celular: celularFormatado,
          senha: '',
        });

        setIdTurmaAtual(participante.idTurma || '');
      } else {
        showToast('Erro ao carregar participante.', 3000, 'error');
        if (participanteResp.message?.toLowerCase().includes('token')) logout();
      }

      hideLoader();
    };

    if (idParticipante) carregarDados();
  }, [idParticipante, isEditing]);

  const breadcrumbLabel = isEditing ? `${formData.nome || 'participante'}` : undefined;

  return (
    <div className={styles.ParticipanteEditPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idParticipante, label: breadcrumbLabel }] : []} />
        <h1 className={styles.title}>Editar Participante</h1>
        <FriendlyPhrase page="atualizarParticipante" />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>
        <label>
          Nome:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          E-mail:
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          Celular:
          <input
            type="tel"
            name="celular"
            value={formData.celular}
            onChange={handleChange}
            required
            placeholder="(11) 91234-5678"
          />
        </label>

        <label>
          Senha:
          <input
            type="password"
            name="senha"
            value={formData.senha}
            onChange={handleChange}
            placeholder="Preencha apenas se quiser alterar"
          />
        </label>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            Atualizar Participante
          </button>
        </div>
      </form>
    </div>
  );
}

export default ParticipanteEditPage;
